import React from "react";
import useAIConfigs from "@/hooks/useAIConfigs";
import { LoadingSpinner } from "@velaro/velaro-component-lib";
import { useNavigate } from "react-router-dom";

export default function AINewSettings() {
  const { aiConfigurations, loading, remove } = useAIConfigs();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const handleEdit = (id: number) => {
    navigate(`/ai-new/settings/general/${id}`);
  };

  const handleDelete = (config: any, index: number) => {
    if (!confirm(`Are you sure you want to delete "${config.name}"?`)) {
      return;
    }
    remove(config, index);
  };

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      active: "bg-green-100 text-green-800",
      inactive: "bg-slate-100 text-slate-800",
      crawling: "bg-blue-100 text-blue-800",
      error: "bg-red-100 text-red-800"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.inactive}`}>
        {status}
      </span>
    );
  };

  if (aiConfigurations.length === 0) {
    return (
      <div className="p-4">
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <div className="text-slate-400 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
          <p className="text-slate-500 mb-4">No chatbot configurations yet</p>
          <p className="text-sm text-slate-400">
            Click "New Chatbot" to create your first AI chatbot configuration
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Chatbot Configurations</h2>
        <p className="text-sm text-slate-500">
          Manage your AI chatbot settings and configurations
        </p>
      </div>

      <div className="bg-white rounded-lg border">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-3 text-sm font-medium text-slate-600">Name</th>
              <th className="text-left p-3 text-sm font-medium text-slate-600">Model</th>
              <th className="text-left p-3 text-sm font-medium text-slate-600">Language</th>
              <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
              <th className="text-right p-3 text-sm font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {aiConfigurations.map((config, index) => (
              <tr key={config.id} className="hover:bg-slate-50">
                <td className="p-3">
                  <span className="font-medium text-sm">{config.name}</span>
                </td>
                <td className="p-3">
                  <span className="text-sm text-slate-600">{config.aiModel}</span>
                </td>
                <td className="p-3">
                  <span className="text-sm text-slate-600">{config.language}</span>
                </td>
                <td className="p-3">
                  {getStatusBadge(config.status)}
                </td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleEdit(config.id)}
                      className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(config, index)}
                      className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
